import { useEffect, useId, useRef, useState } from "react";
import { X } from "lucide-react";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { clientsService, type StaffUser } from "@/services/clientsService";

/**
 * "Add team members" picker for the New client dialog (feature C, design §6;
 * R15, D4). Controlled: ``value`` is the picked user ids, which F4 sends as
 * member_user_ids. Staff come only from clientsService.staffUsers (the
 * server decides the staff set, via apiClient only); the picker lists exactly
 * what it returns and passes the ids through unchanged.
 */
export interface StaffMemberPickerProps {
  value: string[];
  onChange: (ids: string[]) => void;
}

const SEARCH_DEBOUNCE_MS = 300;

function labelOf(user: StaffUser): string {
  return user.display_name || user.email;
}

export function StaffMemberPicker({ value, onChange }: StaffMemberPickerProps) {
  const inputId = useId();
  const [term, setTerm] = useState("");
  const [options, setOptions] = useState<StaffUser[]>([]);
  // Users seen in results, so a chip can show a name for a picked id.
  const [known, setKnown] = useState<Record<string, StaffUser>>({});
  // The term the latest search was requested for: an older answer never wins.
  const requestedRef = useRef("");

  const query = term.trim();

  useEffect(() => {
    requestedRef.current = query;
    if (!query) {
      setOptions([]);
      return;
    }
    const timer = setTimeout(() => {
      clientsService
        .staffUsers(query)
        .then((users) => {
          if (requestedRef.current !== query) return;
          setOptions(users);
        })
        .catch(() => {
          if (requestedRef.current !== query) return;
          setOptions([]);
        });
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  const pick = (user: StaffUser) => {
    setKnown((prev) => ({ ...prev, [user.id]: user }));
    if (!value.includes(user.id)) onChange([...value, user.id]);
    setTerm("");
    setOptions([]);
  };

  const remove = (id: string) => onChange(value.filter((v) => v !== id));

  const visible = options.filter((u) => !value.includes(u.id));

  return (
    <div className="space-y-1">
      <Label htmlFor={inputId} className="text-xs text-muted-foreground">
        Add team members
      </Label>
      <Input
        id={inputId}
        value={term}
        placeholder="Search AgileCo staff by name or email"
        autoComplete="off"
        onChange={(e) => setTerm(e.target.value)}
      />
      {visible.length > 0 && (
        <ul
          role="listbox"
          aria-label="Staff matches"
          className="max-h-48 overflow-auto rounded-md border bg-popover text-sm"
        >
          {visible.map((user) => (
            <li
              key={user.id}
              role="option"
              aria-selected={false}
              tabIndex={0}
              className="flex cursor-pointer flex-col px-2 py-1 hover:bg-accent focus:bg-accent"
              onClick={() => pick(user)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  pick(user);
                }
              }}
            >
              <span>{user.display_name || user.email}</span>
              {user.display_name && (
                <span className="text-xs text-muted-foreground">{user.email}</span>
              )}
            </li>
          ))}
        </ul>
      )}
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1 pt-1">
          {value.map((id) => {
            const user = known[id];
            const label = user ? labelOf(user) : id;
            return (
              <span
                key={id}
                className="inline-flex items-center gap-1 rounded-md bg-muted px-2 py-0.5 text-xs"
              >
                {label}
                <button
                  type="button"
                  aria-label={`Remove ${label}`}
                  className="text-muted-foreground hover:text-foreground"
                  onClick={() => remove(id)}
                >
                  <X className="h-3 w-3" aria-hidden="true" />
                </button>
              </span>
            );
          })}
        </div>
      )}
      <p className="text-xs text-muted-foreground">
        You become owner if a new workspace is created.
      </p>
    </div>
  );
}
